import { CART_COOKIE_NAME } from '../constants/cart-cookie';

export default (context, inject) => {
  const MAX_REQUEST_RETRIES = 2;

  class ApiService {
    constructor () {
      this.axios = null;
      this.createApi();
      this.cancelTokens = [];
    }

    createApi () {
      delete this.axios;
      this.axios = context.$axios.create(this.getBaseSettings());
    }

    getBaseSettings () {
      return {
        xsrfHeaderName: 'something',
        timeout: context.$getEnv('API_TIMEOUT', 20000),
      };
    }

    abortCancellableRequests () {
      this.cancelTokens.forEach((cancelToken) => cancelToken.cancel());
      this.cancelTokens = [];
    }

    /**
     * @param uri
     * @param params
     * @param config
     * @param onReceiveMessage
     */
    // subscribe(uri, params, config, onReceiveMessage) {
    //   config = config || {};
    //   if (typeof config.transformResponse !== 'object') {
    //     config.transformResponse = [];
    //   }
    //   config.transformResponse.push(function () {
    //     return (data) => {
    //       const response = JSON.parse(data);
    //       response.mercureListener = new MercureListener(response, onReceiveMessage || null);
    //       return response;
    //     };
    //   }());
    //   return this.get(uri, params, config);
    // }

    /**
     * @returns {Promise<*>}
     */
    async doRequest (reqConfig = {}, retryCount = 0) {
      const config = {
        api_url: null,
        useSiteEndpoint: false,
        headers: {
          'content-type': 'application/ld+json',
          accept: 'application/ld+json',
        },
        ...reqConfig,
      };

      if (context.$getEnv('SITE_UUID') !== null) {
        config.headers['x-site-context'] = context.$getEnv('SITE_UUID');
      }

      const cartCookie = context.$cookies.get(CART_COOKIE_NAME) || null;
      if (cartCookie !== null && config.url.includes('/carts')) {
        config.headers['x-cart-secret'] = cartCookie;
      }

      if (((context.i18n || {}).localeProperties || {}).api !== undefined) {
        config.headers['accept-language'] = context.i18n.localeProperties.api;
      }

      const useSiteEndpoint = !!config.useSiteEndpoint;
      let baseUrl;
      if (config.api_url) {
        baseUrl = new URL(config.api_url);
        delete config.api_url;
      } else {
        baseUrl = new URL(useSiteEndpoint === false
          ? context.$getEnv('API_URL')
          : context.$getEnv('SITE_URL'));
      }

      let endpoint = config.url;
      if (endpoint.startsWith(baseUrl.pathname)) {
        endpoint = endpoint.replace(baseUrl.pathname, '');
      }

      if (config.url.substr(0, 4) !== 'http') {
        config.url = baseUrl + endpoint;
      }

      if (useSiteEndpoint === false) {
        if (config.client_grant === undefined) {
          const token = await context.$auth.getAccessToken();
          if (token) {
            if (token.expires_at > Math.round(Date().now) / 1000) {
              if (context.$auth.isRefreshing === true) {
                await context.$auth.waitForToken();
              } else {
                await context.$auth.refreshToken();
              }

              return this.doRequest(reqConfig, retryCount + 1);
            }

            if (token.access_token !== null) {
              config.headers = {
                ...config.headers,
                Authorization: `Bearer ${token.access_token}`,
              };
            }
          }
        }
      }

      const cancellable = !!config.params?.cancellable;

      if (context.$isClient()) {
        if (config?.params?.abort_cancellable_requests) {
          this.abortCancellableRequests();
        }

        const source = context.$axios.CancelToken.source();
        config.cancelToken = source.token;

        if (cancellable) {
          this.cancelTokens.push(source);
        }

        if (config?.params) {
          config.params = Object.fromEntries(Object.entries(config.params).filter(([key]) => (
            !['cancellable', 'abort_cancellable_requests'].includes(key)
          )));
        }
      }

      return await this.axios.request(config)
        .then((response) => {
          // console.info('Api request succeeded (' + config.url + '):')
          // console.info(response.status + ' ' + response.statusText)

          return response;
        })
        .catch(async (error) => {
          // console.error('Api request failed (' + config.url + '):')
          // console.error(error.response.status + ' ' + error.response.statusText)
          //
          // if (error.response.data) {
          //   console.error('data', error.response.data)
          // }

          if (Object.prototype.toString.call(error).slice(8, -1) === 'Error') {
            if (
              typeof error?.response?.data?.error_description !== 'undefined'
              && error.response.data.error_description === 'The refresh token is invalid.'
            ) {
              context.$auth.logout();
              throw error;
            }

            if (
              typeof error?.response?.status !== 'undefined'
              && error.response.status === 401
              && retryCount < MAX_REQUEST_RETRIES
            ) {
              if (context.$auth.isRefreshing === true) {
                // console.info('Wait for refresh token..')
                await context.$auth.waitForToken();
              } else {
                // console.info('Refresh token..')
                await context.$auth.refreshToken();
              }

              return this.doRequest(reqConfig, retryCount + 1);
            }

            throw error;
          }

          return null;
        });
    }

    /**
     * Get single entity
     *
     * @param url
     * @param params
     * @param config
     * @returns {Promise<*>}
     */
    get (url = '', params = {}, config = {}) {
      return this.doRequest({
        ...(config || {}),
        url,
        method: 'get',
        params: (params || {}),
      });
    }

    /**
     * Delete single entity
     *
     * @param url
     * @param config
     * @returns {Promise<*>}
     */
    delete (url = '', config = {}) {
      return this.doRequest({
        ...(config || {}),
        url,
        method: 'delete',
      });
    }

    /**
     * Update single entity
     *
     * @param url
     * @param data
     * @param config
     * @returns {Promise<*>}
     */
    post (url = '', data = {}, config = {}) {
      return this.doRequest({
        ...(config || {}),
        url,
        method: 'post',
        data: (data || {}),
      });
    }

    /**
     * @param url
     * @param data
     * @param config
     * @returns {Promise<*>}
     */
    put (url = '', data = {}, config = {}) {
      return this.doRequest({
        ...(config || {}),
        url,
        method: 'put',
        data: (data || {}),
      });
    }

    /**
     * @param url
     * @param data
     * @param config
     * @returns {Promise<*>}
     */
    patch (url = '', data = {}, config = {}) {
      return this.doRequest({
        ...(config || {}),
        url,
        method: 'patch',
        data: (data || {}),
      });
    }
  }

  const apiService = new ApiService();
  inject('api', apiService);
};
